import React, { useState } from 'react';
import { IWorkbookSource, IMarketResearchInsight } from '../../interfaces/domain';
import Button from '../../components/UI/Button/Button';

import classes from './AddAgentTaskPanel.module.css';

/** Available task types for the dropdown */
const TASK_TYPE_OPTIONS = [
  { value: 'RESEARCH_GATEKEEPER', label: '⚖️ Portinvartija — Compliance & lainmukaisuus' },
  { value: 'RESEARCH_FACTORY', label: '🏭 Tehdastyöläinen — Kilpailijat, hinnoittelu & copy' },
  { value: 'RESEARCH_SCAFFOLD', label: '🔧 Scaffolder — React-koodirakenteet' },
  { value: 'SEO_KEYWORDS', label: '🔎 SEO & avainsanat' },
  { value: 'SUPPLIERS_B2B', label: '🤝 B2B-kontaktointi & palveluntarjoajat' },
  { value: 'MARKET_TAM_SAM', label: '📊 TAM/SAM-markkinakoko' },
  { value: 'COMPLIANCE_LEGAL', label: '⚖️ Lainmukaisuus (fokus)' },
  { value: 'COMPETITORS_PRICING', label: '💰 Kilpailijat & hinnoittelu (fokus)' },
  { value: 'CUSTOM_QUERY', label: '✏️ Vapaamuotoinen tutkimuskysymys' },
];

/** Available model types */
const MODEL_TYPE_OPTIONS = [
  { value: 'DEEP_RESEARCH', label: '🧠 Deep Research — Syvällinen, 3–10 min' },
  { value: 'VERTEX_AI', label: '⚡ Gemini Flash — Nopea, ~30s' },
];

const SOURCE_ICONS: Record<string, string> = {
  TEXT: '📝',
  URL: '🔗',
  FILE: '📎',
};

interface IProps {
  onAddTask: (
    taskType: string,
    modelType: string,
    customQuery: string,
    selectedSourceIds?: number[],
    selectedInsightIds?: number[]
  ) => Promise<void>;
  disabled?: boolean;
  sources?: IWorkbookSource[];
  insights?: IMarketResearchInsight[];
}

const getInsightTitle = (insight: IMarketResearchInsight): string => {
  const labelMap: Record<string, { icon: string; label: string }> = {
    RESEARCH_GATEKEEPER: { icon: '⚖️', label: 'Portinvartija' },
    RESEARCH_FACTORY: { icon: '🏭', label: 'Tehdastyöläinen' },
    RESEARCH_SCAFFOLD: { icon: '🔧', label: 'Scaffolder' },
    RESEARCH_FOCUS: { icon: '🔍', label: 'Fokustutkimus' },
    COMPLIANCE_LEGAL: { icon: '⚖️', label: 'Lainmukaisuus' },
    COMPETITORS_PRICING: { icon: '💰', label: 'Kilpailijat & hinnoittelu' },
    SEO_KEYWORDS: { icon: '🔎', label: 'SEO & avainsanat' },
    SUPPLIERS_B2B: { icon: '🤝', label: 'B2B-kontaktointi' },
    MARKET_TAM_SAM: { icon: '📊', label: 'TAM/SAM-analyysi' },
    CUSTOM_QUERY: { icon: '✏️', label: 'Vapaamuotoinen tutkimus' }
  };
  const info = labelMap[insight.focusType] || { icon: '📝', label: insight.focusType };
  return `${info.icon} ${info.label}`;
};

const formatInsightDate = (created?: string): string => {
  if (!created) return '';
  try {
    const d = new Date(created);
    return d.toLocaleDateString('fi-FI') + ' klo ' + d.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return '';
  }
};

const AddAgentTaskPanel: React.FC<IProps> = ({ onAddTask, disabled, sources = [], insights = [] }) => {
  const [taskType, setTaskType] = useState('RESEARCH_GATEKEEPER');
  const [modelType, setModelType] = useState('DEEP_RESEARCH');
  const [customQuery, setCustomQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedSourceIds, setSelectedSourceIds] = useState<Set<string>>(new Set());
  const [selectedInsightIds, setSelectedInsightIds] = useState<Set<string>>(new Set());

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const sourceIds = Array.from(selectedSourceIds).map((id) => parseInt(id));
      const insightIds = Array.from(selectedInsightIds).map((id) => parseInt(id));
      await onAddTask(
        taskType,
        modelType,
        customQuery,
        sourceIds.length > 0 ? sourceIds : undefined,
        insightIds.length > 0 ? insightIds : undefined
      );
      setCustomQuery('');
      setSelectedSourceIds(new Set());
      setSelectedInsightIds(new Set());
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSource = (sourceId: string) => {
    setSelectedSourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(sourceId)) {
        next.delete(sourceId);
      } else {
        next.add(sourceId);
      }
      return next;
    });
  };

  const handleToggleInsight = (insightId: string) => {
    setSelectedInsightIds((prev) => {
      const next = new Set(prev);
      if (next.has(insightId)) {
        next.delete(insightId);
      } else {
        next.add(insightId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const readySources = sources.filter((s) => s.status === 'READY' && s.contentLength && s.contentLength > 0);
    setSelectedSourceIds(new Set(readySources.map((s) => s.id)));
    const readyInsights = insights.filter((i) => i.status === 'COMPLETED' && i.responseText);
    setSelectedInsightIds(new Set(readyInsights.map((i) => i.id)));
  };

  const handleDeselectAll = () => {
    setSelectedSourceIds(new Set());
    setSelectedInsightIds(new Set());
  };

  const isCustom = taskType === 'CUSTOM_QUERY';
  const selectedChars = sources
    .filter((s) => selectedSourceIds.has(s.id))
    .reduce((sum, s) => sum + (s.contentLength || 0), 0) +
    insights
    .filter((i) => selectedInsightIds.has(i.id))
    .reduce((sum, i) => sum + (i.responseText?.length || 0), 0);
  const selectedTokens = Math.round(selectedChars / 3.5);

  return (
    <div className={classes.panel}>
      <h3 className={classes.title}>Lisää tutkimustehtävä</h3>
      
      <div className={classes.form}>
        <div className={classes.row}>
          <div className={classes.field}>
            <label className={classes.label}>Tutkimustyyppi</label>
            <select
              className={classes.select}
              value={taskType}
              onChange={(e) => setTaskType(e.target.value)}
              disabled={disabled || loading}
            >
              {TASK_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className={classes.field}>
            <label className={classes.label}>LLM-malli</label>
            <div className={classes.radioGroup}>
              {MODEL_TYPE_OPTIONS.map((opt) => (
                <label key={opt.value} className={classes.radioLabel}>
                  <input
                    type="radio"
                    name="modelType"
                    value={opt.value}
                    checked={modelType === opt.value}
                    onChange={(e) => setModelType(e.target.value)}
                    disabled={disabled || loading}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {isCustom && (
          <div className={classes.field}>
            <label className={classes.label}>Tutkimuskysymys</label>
            <textarea
              className={classes.textarea}
              value={customQuery}
              onChange={(e) => setCustomQuery(e.target.value)}
              placeholder="Kirjoita vapaamuotoinen tutkimuskysymys tähän..."
              rows={3}
              disabled={disabled || loading}
            />
          </div>
        )}

        {/* Source selection with checkboxes */}
        <div className={classes.field}>
          <label className={classes.label}>
            📓 Lähdeaineisto tehtävälle
            {(selectedSourceIds.size + selectedInsightIds.size) > 0 && (
              <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 8 }}>
                {selectedSourceIds.size + selectedInsightIds.size} valittu · {selectedChars.toLocaleString('fi-FI')} merkkiä (~{selectedTokens.toLocaleString('fi-FI')} tok.)
              </span>
            )}
          </label>

          {sources.length === 0 && insights.length === 0 ? (
            <div style={{ padding: '10px 0', color: '#94a3b8', fontSize: '0.85rem', fontStyle: 'italic' }}>
              ⚠️ Ei lähteitä tai aikaisempia tuloksia — lisää aineistoa tai suorita tehtäviä ennen uuden luontia
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  style={{ 
                    padding: '4px 10px', border: '1px solid #cbd5e1', borderRadius: 4, 
                    background: '#f8fafc', cursor: 'pointer', fontSize: '0.78rem', color: '#475569',
                    fontFamily: 'inherit'
                  }}
                >
                  Valitse kaikki
                </button>
                <button
                  type="button"
                  onClick={handleDeselectAll}
                  style={{ 
                    padding: '4px 10px', border: '1px solid #cbd5e1', borderRadius: 4, 
                    background: '#f8fafc', cursor: 'pointer', fontSize: '0.78rem', color: '#475569',
                    fontFamily: 'inherit'
                  }}
                >
                  Poista valinnat
                </button>
              </div>

              {/* Sub-section 1: Documents and URLs */}
              {sources.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    📁 Dokumentit ja URL-osoitteet ({sources.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {sources.map((source) => {
                      const icon = SOURCE_ICONS[source.sourceType] || '📄';
                      const isDisabled = source.status !== 'READY' || !source.contentLength;
                      return (
                        <label
                          key={source.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '6px 10px', borderRadius: 6,
                            background: selectedSourceIds.has(source.id) ? '#eff6ff' : '#fafbfc',
                            border: `1px solid ${selectedSourceIds.has(source.id) ? '#bfdbfe' : '#e2e8f0'}`,
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            opacity: isDisabled ? 0.5 : 1,
                            fontSize: '0.85rem',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSourceIds.has(source.id)}
                            onChange={() => handleToggleSource(source.id)}
                            disabled={isDisabled || disabled || loading}
                            style={{ margin: 0 }}
                          />
                          <span>{icon}</span>
                          <span style={{ flex: 1, color: '#1e293b', fontWeight: 500 }}>{source.title}</span>
                          <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                            {source.contentLength ? (
                              <>
                                {source.contentLength.toLocaleString('fi-FI')} merkkiä
                                <span style={{ color: '#cbd5e1', margin: '0 4px' }}>|</span>
                                ~{Math.round(source.contentLength / 3.5).toLocaleString('fi-FI')} tok.
                              </>
                            ) : '—'}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Sub-section 2: Previous Research Results */}
              {insights.filter(i => i.status === 'COMPLETED').length > 0 && (
                <div>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    🧠 Aikaisempien tutkimustehtävien tulokset ({insights.filter(i => i.status === 'COMPLETED').length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {insights
                      .filter((insight) => insight.status === 'COMPLETED')
                      .map((insight) => {
                        const title = getInsightTitle(insight);
                        const dateStr = formatInsightDate(insight.created);
                        const chars = insight.responseText?.length || 0;
                        const tokens = Math.round(chars / 3.5);
                        const isSelected = selectedInsightIds.has(insight.id);
                        return (
                          <label
                            key={insight.id}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '6px 10px', borderRadius: 6,
                              background: isSelected ? '#f5f3ff' : '#fafbfc',
                              border: `1px solid ${isSelected ? '#ddd6fe' : '#e2e8f0'}`,
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleInsight(insight.id)}
                              disabled={disabled || loading}
                              style={{ margin: 0 }}
                            />
                            <span style={{ flex: 1, color: '#1e293b', fontWeight: 500 }}>
                              {title} {dateStr && <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 400, marginLeft: 4 }}>({dateStr})</span>}
                            </span>
                            <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                              {chars.toLocaleString('fi-FI')} merkkiä
                              <span style={{ color: '#cbd5e1', margin: '0 4px' }}>|</span>
                              ~{tokens.toLocaleString('fi-FI')} tok.
                            </span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className={classes.actions}>
          <Button 
            onClick={handleSubmit} 
            disabled={disabled || loading || (isCustom && !customQuery.trim())}
          >
            {loading ? 'Lisätään...' : '+ Lisää tehtävä'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddAgentTaskPanel;
